import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppShell, type ViewId } from "@/components/layout/AppShell";
import { ThemeProvider } from "@/components/theme-provider";
import { CalendarMain, type CalendarMonth } from "@/components/views/calendar/CalendarMain";
import { CalendarSidebar } from "@/components/views/calendar/CalendarSidebar";
import { CompaniesSidebar } from "@/components/views/companies/CompaniesSidebar";
import { CompanyMain } from "@/components/views/companies/CompanyMain";
import { PredictionsSidebar } from "@/components/views/predictions/PredictionsSidebar";
import { PredictionsMain } from "@/components/views/predictions/PredictionsMain";
import { NewsSidebar } from "@/components/views/news/NewsSidebar";
import { NewsMain } from "@/components/views/news/NewsMain";
import type { EarningsCalendarItemOut } from "@/api/client";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  // View-local state lifted here so switching tabs preserves selections.
  const [companySymbol, setCompanySymbol] = useState<string | null>(null);
  const [modelSlug, setModelSlug] = useState<string | null>(null);
  const [enabledModels, setEnabledModels] = useState<Record<string, boolean>>({});
  const [predictionDate, setPredictionDate] = useState<string>(
    () => new Date().toISOString().slice(0, 10),
  );
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [selectedEarningsId, setSelectedEarningsId] = useState<number | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<CalendarMonth>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  function onSelectEarnings(item: EarningsCalendarItemOut) {
    setSelectedEarningsId(item.id);
    const d = new Date(item.announcement_date + "T00:00:00");
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    if (year !== calendarMonth.year || month !== calendarMonth.month) {
      setCalendarMonth({ year, month });
    }
  }

  const hasSelection = (view: ViewId): boolean => {
    if (view === "companies") return companySymbol !== null;
    if (view === "predictions") return modelSlug !== null;
    if (view === "news") return sourceId !== null;
    if (view === "calendar") return selectedEarningsId !== null;
    return false;
  };

  const clearSelection = (view: ViewId): void => {
    if (view === "companies") setCompanySymbol(null);
    else if (view === "predictions") setModelSlug(null);
    else if (view === "news") setSourceId(null);
    else if (view === "calendar") setSelectedEarningsId(null);
  };

  const detailTitle = (view: ViewId): string | null => {
    if (view === "companies") return companySymbol;
    if (view === "predictions") return modelSlug;
    if (view === "calendar") return selectedEarningsId !== null ? "Earnings" : null;
    if (view === "news") return sourceId !== null ? "Source" : null;
    return null;
  };

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AppShell
          hasSelection={hasSelection}
          clearSelection={clearSelection}
          detailTitle={detailTitle}
          mobileMainAsPrimary={(view) => view === "calendar"}
          renderSidebar={(view: ViewId) => {
            if (view === "companies")
              return (
                <CompaniesSidebar selected={companySymbol} onSelect={setCompanySymbol} />
              );
            if (view === "predictions")
              return (
                <PredictionsSidebar
                  enabled={enabledModels}
                  setEnabled={setEnabledModels}
                  date={predictionDate}
                  setDate={setPredictionDate}
                  selected={modelSlug}
                  onSelect={setModelSlug}
                />
              );
            if (view === "calendar")
              return (
                <CalendarSidebar
                  selectedId={selectedEarningsId}
                  onSelect={onSelectEarnings}
                />
              );
            return <NewsSidebar selected={sourceId} onSelect={setSourceId} />;
          }}
          renderMain={(view: ViewId) => {
            if (view === "companies") return <CompanyMain symbol={companySymbol} />;
            if (view === "predictions")
              return <PredictionsMain slug={modelSlug} date={predictionDate} />;
            if (view === "calendar")
              return (
                <CalendarMain
                  selectedId={selectedEarningsId}
                  onSelect={onSelectEarnings}
                  calendarMonth={calendarMonth}
                  setCalendarMonth={setCalendarMonth}
                />
              );
            return <NewsMain sourceId={sourceId} />;
          }}
        />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
